import { Injectable, inject } from '@angular/core';
import { FirebaseFirestoreService } from '@firebase/firebase-firestore.service';
import { DocumentReference, Timestamp, collection, onSnapshot, query, where } from 'firebase/firestore';
import { BehaviorSubject, map, Observable, of, zip } from 'rxjs';

export interface UserPresence {
  userId?: string;
  user?: DocumentReference;
  lastSeenTime?: Timestamp;
  loggedOutTime?: Timestamp;
  appearOffline?: boolean;
  author: DocumentReference;
}

@Injectable({
  providedIn: 'root',
})
export class GoCoreChatPresenceService {
  private firebaseFirestore = inject(FirebaseFirestoreService);

  private readonly chatPresencBatchSize = 10;

  /**
   * cache for observable listening to just the last 20 notifications for the currently active user, in descending time order.
   */
  private userPresenceCollectionList$: Observable<UserPresence[]> = null;
  private userPresence$: Observable<UserPresence[]> = null;

  public userPresenceCollectionListSubject$ = new BehaviorSubject<Observable<UserPresence[]>>(null);

  public isUserOnlineObservable(userId: string): Observable<UserPresence[]> {
    const userRef = this.firebaseFirestore.doc(`users/${userId}`);
    return this.firebaseFirestore.collectionValueChanges<UserPresence>('chat-presence', [where('user', '==', userRef)]);
  }

  // REVIEW - deprecated?  Use isUserOnlineObservable() instead as a more generic version
  public getUserPresenceObservable(userUid: string): Observable<UserPresence[]> {
    const userRef = this.firebaseFirestore.doc(`users/${userUid}`);
    this.userPresence$ = this.firebaseFirestore.collectionValueChanges<UserPresence>('chat-presence', [
      where('user', '==', userRef),
    ]);
    return this.userPresence$;
  }

  public addUsersToPresenceObservable(userUids: string[]): void {
    const documentReferences: DocumentReference[] = [];
    for (const userId of userUids) {
      documentReferences.push(this.firebaseFirestore.doc(`users/${userId}`));
    }
    this.getUserListPresenceObservable(documentReferences);
  }

  private getUserListPresenceObservable(userDocumentReferences: DocumentReference[]): Observable<UserPresence[]> {
    // Return empty observable if no references
    if (!userDocumentReferences || userDocumentReferences.length === 0) {
      return new Observable((observer) => {
        observer.next([]);
        observer.complete();
      });
    }

    // Query max 10 items at a time
    const chunkedDocumentRefs = this.chunkArray(userDocumentReferences, this.chatPresencBatchSize);

    const queryObservables = chunkedDocumentRefs.map((userDocumentReferenceChunk) =>
      this.getUserPresencesObservable(userDocumentReferenceChunk)
    );

    this.userPresenceCollectionList$ = zip(queryObservables).pipe(map((array) => array.flat()));
    this.userPresenceCollectionListSubject$.next(this.userPresenceCollectionList$ ?? of([]));

    return this.userPresenceCollectionList$;
  }

  private getUserPresencesObservable(userDocumentReferencesChunk: DocumentReference[]): Observable<UserPresence[]> {
    return new Observable<UserPresence[]>((observer) => {
      const firestoreQuery = query(
        collection(this.firebaseFirestore.firestore, 'chat-presence'),
        where('user', 'in', userDocumentReferencesChunk)
      );

      onSnapshot(
        firestoreQuery,
        (querySnapshot) => {
          const userPresenceList: UserPresence[] = [];
          querySnapshot.forEach((docSnapshot) => {
            const data = docSnapshot.data();
            userPresenceList.push({ userId: data.user.id, ...data } as UserPresence);
          });

          if (this.userPresenceCollectionList$) {
            this.userPresenceCollectionList$ = zip(of(userPresenceList)).pipe(map((array) => array.flat()));
            this.userPresenceCollectionListSubject$.next(this.userPresenceCollectionList$ ?? of([]));
          }

          observer.next(userPresenceList);
        },
        (error) => {
          observer.error(error);
        }
      );
    });
  }

  public isUserOnline(userPresence: UserPresence): boolean {
    if (!userPresence) {
      return false;
    }

    const lastSeenDate = this.convertToDate(userPresence.lastSeenTime?.seconds);
    const loggedOutDate = this.convertToDate(userPresence.loggedOutTime?.seconds);
    const dateNow = new Date();

    if (lastSeenDate) {
      if (userPresence?.appearOffline) {
        return false;
      }

      if (lastSeenDate > loggedOutDate) {
        if (this.dateIsToday(dateNow, lastSeenDate) && this.timeDifferenceLessThanOneMinute(dateNow, lastSeenDate)) {
          return true;
        }
      }

      return false;
    } else {
      return false;
    }
  }

  private convertToDate(seconds: number): Date {
    const milliseconds = seconds * 1000;
    return new Date(milliseconds);
  }

  private dateIsToday(timeNowDate: Date, lastSeenDate: Date): boolean {
    const todayYear = lastSeenDate.getFullYear();
    const todayMonth = lastSeenDate.getMonth();
    const todayDay = lastSeenDate.getDate();

    const checkYear = timeNowDate.getFullYear();
    const checkMonth = timeNowDate.getMonth();
    const checkDay = timeNowDate.getDate();

    return checkYear === todayYear && checkMonth === todayMonth && checkDay === todayDay;
  }

  private timeDifferenceLessThanOneMinute(timeNowDate: Date, lastSeenDate: Date): boolean {
    const timeDifferenceMs = Math.abs(timeNowDate.getTime() - lastSeenDate.getTime());
    const oneMinuteMs = 60 * 1000;

    return timeDifferenceMs <= oneMinuteMs;
  }

  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunkedArray: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunkedArray.push(array.slice(i, i + chunkSize));
    }
    return chunkedArray;
  }
}
